import { useEffect, useState, useRef } from "react";
import api, { apiErr, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Metric } from "@/components/Metric";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download, Receipt, Landmark, Loader2, AlertTriangle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const BAR_COLORS = ["#2495D3", "#1E7AA9", "#5BB4E0", "#0F5A82", "#8CCEC", "#134E6F"];

const CATEGORIES = ["paper", "roll", "ink", "laminate", "substrate", "sheet", "miscellaneous", "other"];
const FINISHES = ["", "Matte", "Gloss", "Semi-gloss", "Satin", "Luster", "Dry-erase", "Chalkboard", "Reflective", "High-tack", "Low-tack", "Perforated", "Mesh", "Frosted", "Clear"];
const MODULES = [
  "paper", "booklet", "large-format", "stickers", "dtf", "embroidery",
  "laser", "direct-print", "channel-letters", "sublimation", "roll-stickers",
];

export default function Purchases() {
  const authToken = localStorage.getItem("pns_token");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ quarters: [], by_supplier: [] });
  const [filters, setFilters] = useState({ supplier: "", date_from: "", date_to: "" });
  const [machines, setMachines] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [repriceEdits, setRepriceEdits] = useState({});
  const [repricing, setRepricing] = useState(false);
  const applyReprice = async () => {
    const items = (importResult?.increases || []).filter((p) => p.suggested_price != null)
      .map((p) => ({ id: p.id, price_override: Number(repriceEdits[p.id] ?? p.suggested_price) }))
      .filter((x) => x.price_override > 0);
    if (!items.length) return;
    setRepricing(true);
    try {
      const { data } = await api.post("/materials/apply-reprice", { items });
      toast.success(`Updated selling price on ${data.updated} material${data.updated > 1 ? "s" : ""}.`);
      setImportResult((r) => ({ ...r, increases: r.increases.map((p) => ({ ...p, suggested_price: null })) }));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    setRepricing(false);
  };
  const fileRef = useRef(null);
  const emptyTally = () => ({ created: 0, updated: 0, skipped: 0, names: [], increases: [], count: 0 });
  const queueRef = useRef({ files: [], idx: 0, tally: emptyTally() });
  const [queuePos, setQueuePos] = useState(null);

  const load = async () => {
    const params = {};
    if (filters.supplier) params.supplier = filters.supplier;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    const { data } = await api.get("/purchases", { params });
    setItems(data);
    const { data: s } = await api.get("/purchases/summary", { params });
    setSummary(s);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);
  useEffect(() => { api.get("/machines").then(({ data }) => setMachines(data)).catch(() => {}); }, []);

  const openImport = () => { setDraft(null); setImportResult(null); setOpen(true); };

  const parseCurrent = async () => {
    const { files, idx } = queueRef.current;
    if (idx >= files.length) { finishQueue(); return; }
    const f = files[idx];
    setQueuePos({ idx: idx + 1, total: files.length });
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/purchases/parse", fd);
      setDraft({
        ...data,
        default_category: data.suggested_category || "other",
        modules: data.suggested_modules || [],
        supplier_unit_multiplier: data.supplier_unit_multiplier || 1,
        supplier_unit_label: data.supplier_unit_label || "",
        supplier_trained: data.supplier_trained !== false,
        pdf_file_id: data.pdf_file_id || "",
        pdf_filename: data.pdf_filename || "",
        update_inventory: true,
        _filename: f.name,
      });
      toast.success(files.length > 1 ? `Parsed ${idx + 1}/${files.length}: ${f.name}` : "Invoice parsed — review and save");
    } catch (err) {
      toast.error(`${f.name}: ${apiErr(err.response?.data?.detail) || err.message}`);
      queueRef.current.tally.skipped += 1;
      queueRef.current.idx = idx + 1;
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
      return parseCurrent();
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    queueRef.current = { files, idx: 0, tally: { ...emptyTally(), count: files.length } };
    setImportResult(null);
    await parseCurrent();
  };

  const finishQueue = () => {
    const t = queueRef.current.tally || emptyTally();
    setImportResult({ created: t.created, updated: t.updated, skipped: t.skipped, names: t.names, increases: t.increases, total: t.count, multi: t.count > 1 });
    setDraft(null);
    setQueuePos(null);
    queueRef.current = { files: [], idx: 0, tally: emptyTally() };
  };

  const skipCurrent = async () => {
    queueRef.current.tally.skipped += 1;
    queueRef.current.idx += 1;
    setDraft(null);
    if (queueRef.current.idx < queueRef.current.files.length) await parseCurrent();
    else finishQueue();
  };

  const setD = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setSup = (k, v) => setDraft((d) => ({ ...d, supplier: { ...d.supplier, [k]: v } }));
  const setLine = (i, k, v) => setDraft((d) => {
    const li = [...d.line_items]; li[i] = { ...li[i], [k]: v }; return { ...d, line_items: li };
  });
  const toggleModule = (m) => setDraft((d) => ({
    ...d, modules: d.modules.includes(m) ? d.modules.filter((x) => x !== m) : [...d.modules, m],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        supplier: draft.supplier,
        invoice_number: draft.invoice_number || "",
        date: draft.date || "",
        po_number: draft.po_number || "",
        currency: draft.currency || "CAD",
        subtotal: Number(draft.subtotal || 0),
        gst: Number(draft.gst || 0),
        pst: Number(draft.pst || 0),
        shipping: Number(draft.shipping || 0),
        total: Number(draft.total || 0),
        default_category: draft.default_category,
        modules: draft.modules,
        update_inventory: draft.update_inventory,
        supplier_unit_multiplier: Number(draft.supplier_unit_multiplier || 1),
        supplier_unit_label: draft.supplier_unit_label || "",
        pdf_file_id: draft.pdf_file_id || "",
        pdf_filename: draft.pdf_filename || "",
        line_items: draft.line_items.map((li) => ({
          code: li.code || "", description: li.description || "", name: li.name || "",
          quantity: Number(li.quantity || 0), unit: li.unit || "",
          unit_price: Number(li.unit_price || 0), line_total: Number(li.line_total || 0),
          import_material: !!li.import,
          material_id: li.material_id || "",
          machine_id: li.machine_id || "",
          ink_volume_ml: li.ink_volume_ml || 0,
          size: li.size || "",
          category: li.category || "",
          lf_subtype: li.lf_subtype || "",
          make_slit: !!li.make_slit,
          finish: li.finish || "",
          unit_multiplier: Number(draft.supplier_unit_multiplier || 1),
        })),
      };
      const { data } = await api.post("/purchases", payload);
      const aff = data.materials_affected || [];
      const created = aff.filter((a) => a.action === "created").length;
      const updated = aff.filter((a) => a.action === "updated").length;
      const skipped = data.import_skipped || 0;
      const t = queueRef.current.tally || (queueRef.current.tally = { ...emptyTally(), count: 1 });
      t.created += created; t.updated += updated; t.skipped += skipped;
      t.names = t.names.concat(data.import_skipped_names || []);
      t.increases = t.increases.concat(data.price_increases || []);
      load();
      queueRef.current.idx += 1;
      if (queueRef.current.idx < queueRef.current.files.length) {
        setDraft(null);
        setSaving(false);
        toast.success(`Saved ${queueRef.current.idx}/${queueRef.current.files.length}. Loading next invoice…`, { duration: 3000 });
        await parseCurrent();
        return;
      }
      finishQueue();
      toast.success(`Import complete · ${t.created} created, ${t.updated} updated${t.skipped ? `, ${t.skipped} skipped` : ""} — no duplicates.`, { duration: 6000 });
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this purchase record? Any stock this invoice added to inventory will be reversed.")) return;
    const { data } = await api.delete(`/purchases/${id}`);
    const n = (data?.reversed || []).length;
    toast.success(n ? `Deleted — reversed inventory on ${n} material(s)` : "Deleted");
    load();
  };

  const exportCsv = async () => {
    const params = {};
    if (filters.supplier) params.supplier = filters.supplier;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    const res = await api.get("/purchases/export.csv", { params, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url; a.download = "purchases.csv"; a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalSpend = items.reduce((a, p) => a + (p.total || 0), 0);
  const totalGst = items.reduce((a, p) => a + (p.gst || 0), 0);
  const totalPst = items.reduce((a, p) => a + (p.pst || 0), 0);

  return (
    <div data-testid="purchases-page">
      <PageHeader title="Purchases" eyebrow="Business Control"
        subtitle="Import supplier invoices from PDF — auto-fill materials & inventory and keep a tax-ready history.">
        <Button variant="outline" onClick={exportCsv} data-testid="purchases-export-button" className="rounded-lg">
          <Download size={15} className="mr-1" /> Export CSV
        </Button>
        <Button onClick={openImport} data-testid="purchase-import-button" className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg">
          <Upload size={16} className="mr-1" /> Import from PDF
        </Button>
      </PageHeader>

      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Metric icon={Receipt} label="Purchases" value={items.length} />
          <Metric icon={Landmark} label="Total spend" value={money(totalSpend)} accent />
          <Metric icon={Landmark} label="GST paid" value={money(totalGst)} />
          <Metric icon={Landmark} label="PST paid" value={money(totalPst)} />
        </div>

        <div className="flex flex-wrap gap-3 items-end bg-white border border-slate-200 rounded-xl p-4">
          <div><Label className="text-xs">Supplier</Label>
            <Input data-testid="purchases-filter-supplier" value={filters.supplier} onChange={(e) => setFilters({ ...filters, supplier: e.target.value })} className="rounded-lg mt-1 w-48" placeholder="e.g. Alfa" /></div>
          <div><Label className="text-xs">From</Label>
            <Input type="date" data-testid="purchases-filter-from" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="rounded-lg mt-1" /></div>
          <div><Label className="text-xs">To</Label>
            <Input type="date" data-testid="purchases-filter-to" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="rounded-lg mt-1" /></div>
          {(filters.supplier || filters.date_from || filters.date_to) && (
            <Button variant="ghost" onClick={() => setFilters({ supplier: "", date_from: "", date_to: "" })} className="rounded-lg text-slate-500">Clear</Button>
          )}
        </div>

        {items.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="purchases-tax-summary">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-head font-bold mb-3">Quarterly tax summary <span className="text-[11px] font-normal text-slate-400">(BC GST/PST)</span></h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    <th className="text-left py-2">Quarter</th>
                    <th className="text-right py-2">Subtotal</th>
                    <th className="text-right py-2">GST</th>
                    <th className="text-right py-2">PST</th>
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.quarters.map((qz) => (
                    <tr key={qz.period} data-testid="tax-quarter-row" className="border-b border-slate-50">
                      <td className="py-2 font-medium">{qz.period}</td>
                      <td className="py-2 text-right num">{money(qz.subtotal)}</td>
                      <td className="py-2 text-right num text-slate-600">{money(qz.gst)}</td>
                      <td className="py-2 text-right num text-slate-600">{money(qz.pst)}</td>
                      <td className="py-2 text-right num font-semibold text-[#2495D3]">{money(qz.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="font-head font-bold mb-3">Spend by supplier</h3>
              <ResponsiveContainer width="100%" height={Math.max(160, summary.by_supplier.length * 46)}>
                <BarChart data={summary.by_supplier} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" tickFormatter={(v) => `$${v}`} fontSize={11} stroke="#94a3b8" />
                  <YAxis type="category" dataKey="company" width={110} fontSize={11} stroke="#64748b" />
                  <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#f1f5f9" }} />
                  <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                    {summary.by_supplier.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Supplier</th>
                <th className="text-left px-4 py-2.5">Invoice #</th>
                <th className="text-right px-4 py-2.5">Subtotal</th>
                <th className="text-right px-4 py-2.5">GST</th>
                <th className="text-right px-4 py-2.5">PST</th>
                <th className="text-right px-4 py-2.5">Shipping</th>
                <th className="text-right px-4 py-2.5">Total</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-testid="purchase-row" className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2.5 num">{p.date}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{p.supplier?.company}</div>
                    <div className="text-[11px] text-slate-400">{(p.materials_affected || []).length} materials</div>
                  </td>
                  <td className="px-4 py-2.5 num text-slate-500">{p.invoice_number}</td>
                  <td className="px-4 py-2.5 text-right num">{money(p.subtotal)}</td>
                  <td className="px-4 py-2.5 text-right num">{money(p.gst)}</td>
                  <td className="px-4 py-2.5 text-right num">{money(p.pst)}</td>
                  <td className="px-4 py-2.5 text-right num">{money(p.shipping)}</td>
                  <td className="px-4 py-2.5 text-right num font-semibold text-[#2495D3]">{money(p.total)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {p.pdf_file_id && (
                        <a data-testid="purchase-view-pdf" href={`${API}/files/${p.pdf_file_id}/download?auth=${encodeURIComponent(authToken)}`}
                          target="_blank" rel="noopener noreferrer" title="View / download the original invoice PDF"
                          className="p-1.5 text-slate-400 hover:text-[#2495D3]"><FileText size={15} /></a>
                      )}
                      <button data-testid="purchase-delete" onClick={() => remove(p.id)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No purchases yet. Import an invoice PDF to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="purchase-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Import purchase from PDF</DialogTitle>
            <DialogDescription className="text-xs text-slate-400">Upload a supplier invoice — we read it automatically. Review before saving.</DialogDescription>
          </DialogHeader>

          {importResult && (
            <div className="space-y-3 py-2" data-testid="import-result">
              <div className="flex items-center gap-2 text-emerald-600"><Receipt size={18} /><span className="font-head font-bold text-lg">Import complete</span></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-emerald-50 p-3 text-center"><div className="num text-2xl font-black text-emerald-700" data-testid="result-created">{importResult.created}</div><div className="text-[10px] uppercase text-slate-500">Created</div></div>
                <div className="rounded-lg bg-blue-50 p-3 text-center"><div className="num text-2xl font-black text-blue-700" data-testid="result-updated">{importResult.updated}</div><div className="text-[10px] uppercase text-slate-500">Updated</div></div>
                <div className="rounded-lg bg-slate-100 p-3 text-center"><div className="num text-2xl font-black text-slate-600" data-testid="result-skipped">{importResult.skipped}</div><div className="text-[10px] uppercase text-slate-500">Skipped</div></div>
              </div>
              {importResult.multi && <p className="text-xs font-semibold text-slate-600" data-testid="result-multi-total">Processed {importResult.total} invoice{importResult.total > 1 ? "s" : ""} in this batch.</p>}
              <p className="text-xs text-slate-400">No duplicates were created — existing materials were matched by code/description and updated in place.</p>
              {importResult.names?.length > 0 && (
                <details className="rounded-lg border border-slate-200 p-3" data-testid="result-skipped-details">
                  <summary className="text-xs font-semibold cursor-pointer text-slate-600">Show {importResult.names.length} skipped line{importResult.names.length > 1 ? "s" : ""} (services / not imported)</summary>
                  <ul className="mt-2 space-y-1">
                    {importResult.names.map((n, i) => <li key={i} className="text-xs text-slate-500 flex items-center gap-2" data-testid={`result-skipped-name-${i}`}><span className="text-slate-300">•</span>{n || "line"}</li>)}
                  </ul>
                </details>
              )}
              {importResult.increases?.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="result-price-increases">
                  <div className="text-xs font-semibold text-amber-800">⚠︎ Cost changed on {importResult.increases.length} material{importResult.increases.length > 1 ? "s" : ""} — review the suggested selling prices before applying:</div>
                  <ul className="space-y-1.5">
                    {importResult.increases.map((p, i) => (
                      <li key={i} className="text-xs text-amber-900 flex items-center gap-2" data-testid={`result-increase-${i}`}>
                        <span className="flex-1 truncate">{p.name} <span className="font-mono">({p.pct > 0 ? "+" : ""}{p.pct}%)</span> <span className="text-amber-700">{money(p.old_cost)}→{money(p.new_cost)}/unit</span></span>
                        {p.suggested_price != null ? (
                          <>
                            <span className="text-[10px] text-amber-700 shrink-0">price {money(p.old_price)}→</span>
                            <input type="number" step="0.01" data-testid={`reprice-input-${i}`}
                              className="num h-7 w-20 rounded border border-amber-300 px-2 text-right shrink-0"
                              value={repriceEdits[p.id] ?? p.suggested_price}
                              onChange={(e) => setRepriceEdits((m) => ({ ...m, [p.id]: e.target.value }))} />
                          </>
                        ) : (<span className="text-[10px] text-amber-600 shrink-0">{p.old_price ? "applied ✓" : "auto (cost×markup)"}</span>)}
                      </li>
                    ))}
                  </ul>
                  {importResult.increases.some((p) => p.suggested_price != null) && (
                    <Button data-testid="apply-reprice-button" onClick={applyReprice} disabled={repricing} className="bg-amber-600 hover:bg-amber-700 rounded-lg h-8 text-xs">
                      {repricing ? "Applying…" : "Apply new selling prices"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {!importResult && (!draft ? (
            <div className="py-8">
              <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 rounded-xl p-12 cursor-pointer hover:border-[#2495D3] transition-colors" data-testid="purchase-dropzone">
                {uploading ? <Loader2 className="animate-spin text-[#2495D3]" size={32} /> : <FileText className="text-slate-400" size={32} />}
                <span className="text-sm text-slate-500">{uploading ? "Reading invoice…" : "Click to choose PDF invoices — select one or several"}</span>
                <span className="text-[11px] text-slate-400 -mt-2">Multiple files are processed one by one · suppliers can be mixed</span>
                <input ref={fileRef} type="file" accept="application/pdf" multiple className="hidden" data-testid="purchase-file-input" onChange={onFile} disabled={uploading} />
              </label>
            </div>
          ) : (
            <div className="space-y-5 py-1">
              {queuePos && queuePos.total > 1 && (
                <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="import-queue-progress">
                  <span className="text-xs font-semibold text-slate-600">Invoice {queuePos.idx} of {queuePos.total}{draft?._filename ? ` · ${draft._filename}` : ""}</span>
                  <button type="button" onClick={skipCurrent} data-testid="import-skip-button" className="text-xs font-medium text-slate-500 hover:text-red-500">Skip this file →</button>
                </div>
              )}
              {draft.already_imported && (
                <div data-testid="already-imported-warning" className="flex items-start gap-2.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5">
                  <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-red-800">
                    <span className="font-semibold">This invoice was already imported{draft.already_imported_on ? ` on ${draft.already_imported_on}` : ""}.</span> Saving is blocked to avoid duplicating stock. If you need to re-import it, delete the existing purchase first (Purchases list → trash icon).
                  </div>
                </div>
              )}
              {!draft.supplier_trained && (
                <div data-testid="supplier-untrained-warning" className="flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-800">
                    <span className="font-semibold">Supplier not trained in this environment yet.</span> Double-check the <span className="font-semibold">Unit ×</span> (e.g. 1000 for “M Sheets”) and each line’s unit price before saving — otherwise per-thousand pricing may be read as per-unit. Saving will train it for next time.
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2"><Label className="text-xs">Supplier</Label>
                  <Input data-testid="draft-supplier-company" value={draft.supplier?.company || ""} onChange={(e) => setSup("company", e.target.value)} className="rounded-lg mt-1" /></div>
                <div><Label className="text-xs">Invoice #</Label>
                  <Input data-testid="draft-invoice" value={draft.invoice_number || ""} onChange={(e) => setD("invoice_number", e.target.value)} className="rounded-lg mt-1" /></div>
                <div><Label className="text-xs">Date</Label>
                  <Input type="date" data-testid="draft-date" value={draft.date || ""} onChange={(e) => setD("date", e.target.value)} className="rounded-lg mt-1" /></div>
                <div><Label className="text-xs">Supplier email</Label>
                  <Input value={draft.supplier?.email || ""} onChange={(e) => setSup("email", e.target.value)} className="rounded-lg mt-1" /></div>
                <div><Label className="text-xs">Unit × (per-unit qty)</Label>
                  <Input type="number" data-testid="draft-unit-multiplier" value={draft.supplier_unit_multiplier ?? 1} onChange={(e) => setD("supplier_unit_multiplier", e.target.value)} className="rounded-lg mt-1 num" placeholder="1000 for M Sheets" /></div>
                <div><Label className="text-xs">Category (new materials)</Label>
                  <Select value={draft.default_category} onValueChange={(v) => setD("default_category", v)}>
                    <SelectTrigger data-testid="draft-category" className="rounded-lg mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Assign to modules</div>
                <div className="flex flex-wrap gap-2">
                  {MODULES.map((m) => (
                    <button key={m} type="button" data-testid={`draft-module-${m}`} onClick={() => toggleModule(m)}
                      className={`text-xs rounded-full px-3 py-1 border transition-colors ${draft.modules.includes(m) ? "bg-[#2495D3] text-white border-[#2495D3]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 px-1">Line items — review before saving</div>
                {draft.line_items.map((li, i) => {
                  const mult = Number(draft.supplier_unit_multiplier || 1);
                  const stockUnits = li.converted_qty != null ? Number(li.converted_qty) : (Number(li.quantity) || 0) * mult;
                  const perUnit = li.converted_unit_cost != null ? Number(li.converted_unit_cost)
                    : (stockUnits ? (Number(li.line_total) || 0) / stockUnits : (mult ? (Number(li.unit_price) || 0) / mult : 0));
                  const unitLabel = li.stock_unit_label || "";
                  return (
                  <div key={li.code || li.name || i} data-testid="draft-line-row"
                    className={`rounded-lg border p-3 space-y-2 ${li.import ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50/60 opacity-70"}`}>
                    {/* Full-width, readable NAME row */}
                    <div className="flex items-center gap-2.5">
                      <Switch data-testid={`draft-line-import-${i}`} checked={!!li.import} onCheckedChange={(v) => setLine(i, "import", v)} />
                      <Input data-testid={`draft-line-name-${i}`} value={li.name || ""} onChange={(e) => setLine(i, "name", e.target.value)}
                        placeholder="Product name" className="rounded h-8 text-sm flex-1 font-medium" />
                    </div>
                    {li.matched_name && <div className="text-[10px] text-emerald-600 pl-11" data-testid={`draft-line-matched-${i}`}>↳ matches existing: {li.matched_name}</div>}
                    {li.category === "roll" && li.slit_detected && (
                      <div className="text-[10px] text-amber-700 pl-11" data-testid={`draft-line-slit-${i}`}>↳ Ships SLIT IN HALF → the {li.roll_width}" master is NOT stocked; adds 2× {li.roll_width ? li.roll_width / 2 : 27}" rolls to the slit sibling (same $/ft²).</div>
                    )}
                    {li.category === "service" && <div className="text-[10px] text-amber-600 pl-11" data-testid={`draft-line-service-${i}`}>↳ service / fee — not added to inventory</div>}
                    {li.category === "miscellaneous" && li.needs_pieces && <div className="text-[10px] text-amber-700 pl-11" data-testid={`draft-line-needpieces-${i}`}>↳ Pieces not found in the title — enter the number of pieces in Qty before saving.</div>}
                    {li.category === "miscellaneous" && !li.needs_pieces && <div className="text-[10px] text-slate-500 pl-11" data-testid={`draft-line-misc-${i}`}>↳ Miscellaneous add-on · {li.quantity} pcs · sells at cost × {li.misc_markup_x || 2} (adjust per item in Materials)</div>}
                    {/* Fields row (wraps to fit) */}
                    <div className="flex flex-wrap items-end gap-x-3 gap-y-2 pl-11 text-xs">
                      <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Code</span>
                        <Input value={li.code || ""} onChange={(e) => setLine(i, "code", e.target.value)} className="rounded h-7 text-xs w-32" /></label>
                      <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Category</span>
                        <select data-testid={`draft-line-category-${i}`} value={li.category || "other"} onChange={(e) => setLine(i, "category", e.target.value)}
                          className="rounded h-7 text-xs border border-slate-200 bg-white px-1 focus:outline-none focus:ring-1 focus:ring-[#2495D3]">
                          {["roll", "substrate", "paper", "sheet", "ink", "laminate", "miscellaneous", "service", "other"].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select></label>
                      {li.category === "roll" && (
                        <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">LF Subtype</span>
                          <select data-testid={`draft-line-subtype-${i}`} value={li.lf_subtype || "vinyl"} onChange={(e) => setLine(i, "lf_subtype", e.target.value)}
                            className="rounded h-7 text-xs border border-slate-200 bg-white px-1 focus:outline-none focus:ring-1 focus:ring-[#2495D3]">
                            {["poster", "vinyl", "banner", "tshirt", "transfer", "lamination", "special"].map((c) => <option key={c} value={c}>{c}</option>)}
                          </select></label>
                      )}
                      {li.category === "roll" && (
                        <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Finish</span>
                          <select data-testid={`draft-line-finish-${i}`} value={li.finish || ""} onChange={(e) => setLine(i, "finish", e.target.value)}
                            className="rounded h-7 text-xs border border-slate-200 bg-white px-1 focus:outline-none focus:ring-1 focus:ring-[#2495D3]">
                            {(FINISHES.includes(li.finish || "") ? FINISHES : [li.finish, ...FINISHES]).map((c) => <option key={c} value={c}>{c || "— finish —"}</option>)}
                          </select></label>
                      )}
                      {li.category === "roll" && !li.slit_detected && (
                        <label className="flex items-center gap-1.5 self-end h-7 cursor-pointer" data-testid={`draft-line-makeslit-${i}`}>
                          <input type="checkbox" checked={!!li.make_slit} onChange={(e) => setLine(i, "make_slit", e.target.checked)} className="accent-amber-600 w-3.5 h-3.5" />
                          <span className="text-[10px] text-slate-500">Also create {li.roll_width ? li.roll_width / 2 : 27}" slit</span>
                        </label>
                      )}
                      <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Size</span>
                        <Input data-testid={`draft-line-size-${i}`} value={li.size || ""} onChange={(e) => setLine(i, "size", e.target.value)} placeholder="12x18" className="rounded h-7 text-xs w-20" /></label>
                      <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">{li.category === "miscellaneous" ? "Pieces" : "Qty"}</span>
                        <Input type="number" value={li.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="rounded h-7 text-xs w-16 text-right num" /></label>
                      <label className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">Unit price</span>
                        <Input type="number" value={li.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="rounded h-7 text-xs w-24 text-right num" /></label>
                      <div className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">→ Stock</span>
                        <span className="num font-semibold text-slate-700 h-7 flex items-center" data-testid={`draft-line-stock-${i}`}>{stockUnits.toLocaleString()}{unitLabel ? ` ${unitLabel}` : ""}</span></div>
                      <div className="flex flex-col gap-0.5"><span className="text-[9px] font-mono uppercase text-slate-400">→ $/unit</span>
                        <span className="num text-[#2495D3] h-7 flex items-center" data-testid={`draft-line-percost-${i}`}>{money(perUnit)}{unitLabel === "ft²" ? "/ft²" : ""}</span></div>
                    </div>
                    {li.category === "ink" && (
                      <div className="flex flex-wrap gap-2 pl-11">
                        <select data-testid={`draft-line-machine-${i}`} value={li.machine_id || ""} onChange={(e) => setLine(i, "machine_id", e.target.value)}
                          className="rounded h-7 text-xs border border-slate-200 bg-white px-1 focus:outline-none focus:ring-1 focus:ring-[#2495D3]">
                          <option value="">— machine —</option>
                          {machines.map((mm) => <option key={mm.id} value={mm.id}>{mm.name}</option>)}
                        </select>
                        <select data-testid={`draft-line-volume-${i}`} value={li.ink_volume_ml || ""} onChange={(e) => setLine(i, "ink_volume_ml", Number(e.target.value))}
                          className="rounded h-7 text-xs border border-slate-200 bg-white px-1 focus:outline-none focus:ring-1 focus:ring-[#2495D3]">
                          <option value="">— volume —</option>
                          <option value="1000">1L (1000 ml)</option>
                          <option value="440">440 ml</option>
                          <option value="220">220 ml</option>
                        </select>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-5 gap-3">
                {["subtotal", "gst", "pst", "shipping", "total"].map((k) => (
                  <div key={k}><Label className="text-xs capitalize">{k}</Label>
                    <Input type="number" data-testid={`draft-${k}`} value={draft[k] ?? 0} onChange={(e) => setD(k, e.target.value)} className="rounded-lg mt-1 num" /></div>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <Switch data-testid="draft-update-inventory" checked={!!draft.update_inventory} onCheckedChange={(v) => setD("update_inventory", v)} />
                <Label className="text-xs">Update materials & inventory (match by code, add qty to stock, create new if missing)</Label>
              </div>
              <p className="text-[11px] text-slate-400 -mt-1">Rolls (vinyl/banner) convert YD/FT → ft² of stock; substrates (ACM/coroplast/acrylic) store sheets. Stock & $/unit are recalculated on save from each line's category.</p>
            </div>
          ))}

          <DialogFooter>
            {importResult ? (
              <Button data-testid="import-done-button" onClick={() => { setImportResult(null); setDraft(null); setOpen(false); }} className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg">Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancel</Button>
                {draft && (
                  <Button data-testid="purchase-save-button" onClick={save} disabled={saving || !!draft.already_imported} className="bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg disabled:opacity-50">
                    {saving ? "Saving…" : (draft.already_imported ? "Already imported" : (queuePos && queuePos.idx < queuePos.total ? "Save & next" : "Save purchase"))}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
